import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { NotFoundPage, RouteErrorPage } from '@/features/errors/ErrorPages';
import { ModuleFoundationPage } from '@/features/modules/ModuleFoundationPage';
import { AppLayout } from './AppLayout';
import { FullPageSpinner, PublicOnly, RequireAccess, RequireAuth } from './guards';
import { NAVIGATION, type AppNavItem } from './navigation';

/** Code-splits a named export of a feature module. */
function lazyPage<M, K extends keyof M>(loader: () => Promise<M>, name: K) {
  const Component = lazy(() =>
    loader().then((m) => ({ default: m[name] as unknown as ComponentType })),
  );
  return (
    <Suspense fallback={<FullPageSpinner label="Loading…" />}>
      <Component />
    </Suspense>
  );
}

const auth = () => import('@/features/auth/AuthPages');
const reportCards = () => import('@/features/report-cards/ReportingPeriodsPage');

/** Report card administration / records route guards (mirror navigation.ts). */
const REPORT_CARD_ADMIN = { anyOf: ['report_card.manage', 'transcript.read', 'transcript.manage'] };
const CARD_READERS = { anyOf: ['report_card.manage', 'transcript.read', 'transcript.manage'] };
const RECORD_READERS = { anyOf: ['transcript.read', 'transcript.manage'] };
const guarded = (requires: AppNavItem['requires'], element: ReactNode) => (
  <RequireAccess requires={requires}>{element}</RequireAccess>
);

/** One lazy component for /calendar and /calendar/events/:eventId (no remount between them). */
const CALENDAR_PAGE = lazyPage(() => import('@/features/calendar/CalendarPage'), 'CalendarPage');

/** Screens implemented in this phase, keyed by navigation id. */
const AVAILABLE: Record<string, ReactNode> = {
  dashboard: lazyPage(() => import('@/features/dashboard/DashboardPage'), 'DashboardPage'),
  staff: lazyPage(() => import('@/features/staff/StaffDirectoryPage'), 'StaffPage'),
  teachers: lazyPage(() => import('@/features/staff/StaffDirectoryPage'), 'TeachersPage'),
  officials: lazyPage(() => import('@/features/staff/StaffDirectoryPage'), 'OfficialsPage'),
  'admin-schools': lazyPage(() => import('@/features/admin/schools/SchoolsPage'), 'SchoolsPage'),
  'admin-districts': lazyPage(
    () => import('@/features/admin/structure/DistrictsPage'),
    'DistrictsPage',
  ),
  'admin-grade-levels': lazyPage(
    () => import('@/features/admin/structure/GradeLevelsPage'),
    'GradeLevelsPage',
  ),
  'admin-academic-years': lazyPage(
    () => import('@/features/admin/academic-years/AcademicYearsPage'),
    'AcademicYearsPage',
  ),
  'admin-users': lazyPage(() => import('@/features/admin/users/UsersPage'), 'UsersPage'),
  'admin-roles': lazyPage(() => import('@/features/admin/roles/RolesPage'), 'RolesPage'),
  'admin-invitations': lazyPage(
    () => import('@/features/admin/invitations/InvitationsPage'),
    'InvitationsPage',
  ),
  'admin-settings': lazyPage(
    () => import('@/features/admin/settings/SettingsPage'),
    'SettingsPage',
  ),
  'admin-audit-logs': lazyPage(
    () => import('@/features/admin/audit/AuditLogsPage'),
    'AuditLogsPage',
  ),
  platform: lazyPage(() => import('@/features/platform/OrganizationsPage'), 'OrganizationsPage'),
  students: lazyPage(() => import('@/features/students/StudentsPage'), 'StudentsPage'),
  'student-ids': lazyPage(() => import('@/features/student-ids/StudentIdPage'), 'StudentIdPage'),
  guardians: lazyPage(() => import('@/features/guardians/GuardiansPage'), 'GuardiansPage'),
  households: lazyPage(() => import('@/features/households/HouseholdsPage'), 'HouseholdsPage'),
  // Superseded by the Documents library; old links land there.
  'enrollment-documents': <Navigate to="/documents" replace />,
  documents: lazyPage(() => import('@/features/documents/DocumentsPage'), 'DocumentsPage'),
  'documents-retention': lazyPage(
    () => import('@/features/documents/RetentionReviewPage'),
    'RetentionReviewPage',
  ),
  records: lazyPage(
    () => import('@/features/records/RecordsWorkspacePage'),
    'RecordsWorkspacePage',
  ),
  'records-requests': lazyPage(
    () => import('@/features/records/RecordsRequestsPage'),
    'RecordsRequestsPage',
  ),
  registration: lazyPage(
    () => import('@/features/enrollment/RegistrationPage'),
    'RegistrationPage',
  ),
  applications: lazyPage(
    () => import('@/features/enrollment/ApplicationsPage'),
    'ApplicationsPage',
  ),
  forms: lazyPage(() => import('@/features/forms/FormsPage'), 'FormsPage'),
  'live-buses': lazyPage(() => import('@/features/transportation/LiveBusesPage'), 'LiveBusesPage'),
  'driver-console': lazyPage(
    () => import('@/features/transportation/DriverConsolePage'),
    'DriverConsolePage',
  ),
  buses: lazyPage(() => import('@/features/transportation/VehiclesPage'), 'VehiclesPage'),
  routes: lazyPage(() => import('@/features/transportation/RoutesPage'), 'RoutesPage'),
  'transportation-dashboard': lazyPage(
    () => import('@/features/transportation/TransportationDashboardPage'),
    'TransportationDashboardPage',
  ),
  stops: lazyPage(() => import('@/features/transportation/StopsPage'), 'StopsPage'),
  'student-transportation': lazyPage(
    () => import('@/features/transportation/StudentTransportationPage'),
    'StudentTransportationPage',
  ),
  'transportation-settings': lazyPage(
    () => import('@/features/transportation/TransportationSettingsPage'),
    'TransportationSettingsPage',
  ),
  'transportation-exceptions': lazyPage(
    () => import('@/features/transportation/ExceptionsPage'),
    'ExceptionsPage',
  ),
  'transportation-requests': lazyPage(
    () => import('@/features/transportation/ChangeRequestsPage'),
    'ChangeRequestsPage',
  ),
  'admin-integrations': lazyPage(
    () => import('@/features/admin/integrations/IntegrationsPage'),
    'IntegrationsPage',
  ),
  courses: lazyPage(() => import('@/features/academics/CoursesPage'), 'CoursesPage'),
  classes: lazyPage(() => import('@/features/academics/SectionsPage'), 'SectionsPage'),
  scheduling: lazyPage(
    () => import('@/features/academics/ScheduleBuilderPage'),
    'ScheduleBuilderPage',
  ),
  'teaching-dashboard': lazyPage(
    () => import('@/features/teaching/MyDashboardPage'),
    'MyDashboardPage',
  ),
  'my-classes': lazyPage(() => import('@/features/teaching/MyClassesPage'), 'MyClassesPage'),
  'my-schedule': lazyPage(() => import('@/features/teaching/MySchedulePage'), 'MySchedulePage'),
  'lesson-plans': lazyPage(
    () => import('@/features/teaching/LessonPlannerPage'),
    'LessonPlannerPage',
  ),
  assignments: lazyPage(() => import('@/features/teaching/AssignmentsPage'), 'AssignmentsPage'),
  gradebook: lazyPage(() => import('@/features/teaching/GradebookPage'), 'GradebookPage'),
  'student-alerts': lazyPage(() => import('@/features/teaching/AlertsPage'), 'AlertsPage'),
  'teaching-report-cards': lazyPage(
    () => import('@/features/report-cards/MyReportCardsPage'),
    'MyReportCardsPage',
  ),
  'report-cards': lazyPage(reportCards, 'ReportCardsPage'),
  'progress-reports': lazyPage(reportCards, 'ProgressReportsPage'),
  credits: lazyPage(() => import('@/features/records/CreditsPage'), 'CreditsPage'),
  graduation: lazyPage(() => import('@/features/records/GraduationPage'), 'GraduationPage'),
  transcripts: lazyPage(() => import('@/features/records/TranscriptsPage'), 'TranscriptsPage'),
  'gpa-policies': lazyPage(() => import('@/features/records/GpaPoliciesPage'), 'GpaPoliciesPage'),
  // Guidance (counselor workspace) and special programs (IEP/504).
  guidance: lazyPage(
    () => import('@/features/guidance/GuidanceDashboardPage'),
    'GuidanceDashboardPage',
  ),
  'guidance-caseload': lazyPage(() => import('@/features/guidance/CaseloadPage'), 'CaseloadPage'),
  'guidance-appointments': lazyPage(
    () => import('@/features/guidance/AppointmentsPage'),
    'AppointmentsPage',
  ),
  'guidance-referrals': lazyPage(
    () => import('@/features/guidance/ReferralsPages'),
    'ReferralsPage',
  ),
  'guidance-interventions': lazyPage(
    () => import('@/features/guidance/InterventionsPage'),
    'InterventionsPage',
  ),
  'guidance-follow-ups': lazyPage(() => import('@/features/guidance/FollowUps'), 'FollowUpsPage'),
  'guidance-course-requests': lazyPage(
    () => import('@/features/guidance/Requests'),
    'CourseRequestsPage',
  ),
  'guidance-schedule-changes': lazyPage(
    () => import('@/features/guidance/Requests'),
    'ScheduleChangesPage',
  ),
  'guidance-scholarships': lazyPage(
    () => import('@/features/guidance/ScholarshipsPage'),
    'ScholarshipsPage',
  ),
  'counseling-referrals': lazyPage(
    () => import('@/features/guidance/ReferralsPages'),
    'MyReferralsPage',
  ),
  'special-programs': lazyPage(
    () => import('@/features/special-programs/SpecialProgramsPages'),
    'SpecialProgramsDashboardPage',
  ),
  'iep-504': lazyPage(
    () => import('@/features/special-programs/SpecialProgramsPages'),
    'PlansPage',
  ),
  'special-programs-participation': lazyPage(
    () => import('@/features/special-programs/SpecialProgramsPages'),
    'ParticipationPage',
  ),
  'teaching-accommodations': lazyPage(
    () => import('@/features/special-programs/TeacherAccommodations'),
    'TeacherAccommodationsPage',
  ),
  // Discipline + detention, digital passes, communications.
  discipline: lazyPage(
    () => import('@/features/discipline/DisciplineDashboardPage'),
    'DisciplineDashboardPage',
  ),
  'discipline-incidents': lazyPage(
    () => import('@/features/discipline/IncidentsPage'),
    'IncidentsPage',
  ),
  'discipline-referrals': lazyPage(
    () => import('@/features/discipline/ReferralsPage'),
    'ReferralsPage',
  ),
  detention: lazyPage(() => import('@/features/discipline/DetentionPage'), 'DetentionPage'),
  'discipline-settings': lazyPage(
    () => import('@/features/discipline/DisciplineSettingsPage'),
    'DisciplineSettingsPage',
  ),
  'teaching-referrals': lazyPage(
    () => import('@/features/discipline/MyReferralsPage'),
    'MyReferralsPage',
  ),
  'hall-passes': lazyPage(() => import('@/features/passes/ActivePassesPage'), 'ActivePassesPage'),
  'hall-pass-records': lazyPage(
    () => import('@/features/passes/PassRecordsPage'),
    'PassRecordsPage',
  ),
  'hall-pass-settings': lazyPage(
    () => import('@/features/passes/PassSettingsPage'),
    'PassSettingsPage',
  ),
  'teaching-passes': lazyPage(
    () => import('@/features/passes/TeacherPassesPage'),
    'TeacherPassesPage',
  ),
  'nurse-office': lazyPage(() => import('@/features/health/NurseOfficePage'), 'NurseOfficePage'),
  'nurse-visit-reasons': lazyPage(
    () => import('@/features/health/VisitReasonsPage'),
    'VisitReasonsPage',
  ),
  // Communication Center (COMMS): messages, templates, groups, settings; event routing rules.
  communications: lazyPage(() => import('@/features/communications/MessagesPage'), 'MessagesPage'),
  'communications-templates': lazyPage(
    () => import('@/features/communications/TemplatesPage'),
    'TemplatesPage',
  ),
  'communications-groups': lazyPage(
    () => import('@/features/communications/CommunicationSettingsPage'),
    'GroupsPage',
  ),
  'communications-routing': lazyPage(
    () => import('@/features/communications/CommunicationsPage'),
    'CommunicationsPage',
  ),
  'communications-settings': lazyPage(
    () => import('@/features/communications/CommunicationSettingsPage'),
    'CommunicationSettingsPage',
  ),
  'teaching-attendance': lazyPage(
    () => import('@/features/attendance/TeacherAttendancePage'),
    'TeacherAttendancePage',
  ),
  'daily-attendance': lazyPage(
    () => import('@/features/attendance/DailyAttendancePage'),
    'DailyAttendancePage',
  ),
  'check-in-out': lazyPage(() => import('@/features/attendance/CheckInOutPage'), 'CheckInOutPage'),
  'attendance-office': lazyPage(
    () => import('@/features/attendance/AttendanceOfficePage'),
    'AttendanceOfficePage',
  ),
  'attendance-settings': lazyPage(
    () => import('@/features/attendance/AttendanceSettingsPage'),
    'AttendanceSettingsPage',
  ),
  'front-office-dashboard': lazyPage(
    () => import('@/features/front-office/FrontOfficePage'),
    'FrontOfficePage',
  ),
  visitors: lazyPage(() => import('@/features/front-office/VisitorsPage'), 'VisitorsPage'),
  'visitor-approvals': lazyPage(
    () => import('@/features/front-office/VisitorsPage'),
    'VisitorApprovalsPage',
  ),
  'student-calls': lazyPage(() => import('@/features/front-office/DeskPages'), 'StudentCallsPage'),
  'student-messages': lazyPage(() => import('@/features/front-office/DeskPages'), 'MessagesPage'),
  'front-office-appointments': lazyPage(
    () => import('@/features/front-office/AppointmentsPage'),
    'AppointmentsPage',
  ),
  'temp-ids': lazyPage(() => import('@/features/front-office/DeskPages'), 'TempIdsPage'),
  'transportation-changes': lazyPage(
    () => import('@/features/front-office/TransportationChangesPage'),
    'TransportationChangesPage',
  ),
  'lost-found': lazyPage(() => import('@/features/front-office/LostFoundPage'), 'LostFoundPage'),
  'late-arrivals': lazyPage(() => import('@/features/front-office/DeskPages'), 'LateArrivalsPage'),
  'emergency-contacts': lazyPage(
    () => import('@/features/front-office/DeskPages'),
    'EmergencyContactsPage',
  ),
  'lunch-dashboard': lazyPage(
    () => import('@/features/lunch/LunchDashboardPage'),
    'LunchDashboardPage',
  ),
  'lunch-pos': lazyPage(() => import('@/features/lunch/PosPage'), 'PosPage'),
  'lunch-accounts': lazyPage(() => import('@/features/lunch/AccountsPage'), 'AccountsPage'),
  'lunch-menus': lazyPage(() => import('@/features/lunch/MenusPage'), 'MenusPage'),
  'lunch-setup': lazyPage(() => import('@/features/lunch/SetupPage'), 'LunchSetupPage'),
  'budgets-mine': lazyPage(() => import('@/features/budgets/BudgetsPage'), 'BudgetsPage'),
  'purchase-requests': lazyPage(
    () => import('@/features/budgets/PurchaseRequestsPage'),
    'PurchaseRequestsPage',
  ),
  'budget-approvals': lazyPage(
    () => import('@/features/budgets/PurchaseRequestsPage'),
    'ApprovalsPage',
  ),
  'budget-setup': lazyPage(() => import('@/features/budgets/BudgetSetupPage'), 'BudgetSetupPage'),
  calendar: CALENDAR_PAGE,
  'student-id-print-jobs': lazyPage(
    () => import('@/features/student-ids/PrintJobsPage'),
    'PrintJobsPage',
  ),
};

/** Every navigation leaf becomes a guarded route: hidden items are also unreachable by URL. */
function navigationRoutes(): RouteObject[] {
  const routes: RouteObject[] = [];
  const walk = (items: AppNavItem[], section?: string) => {
    for (const item of items) {
      if (item.children) {
        walk(item.children, item.label);
        continue;
      }
      if (!item.path) continue;
      const element =
        item.status === 'planned' ? (
          <ModuleFoundationPage item={item} section={section} />
        ) : (
          (AVAILABLE[item.id] ?? <NotFoundPage />)
        );
      routes.push({ path: item.path.slice(1), element: guarded(item.requires, element) });
    }
  };
  for (const s of NAVIGATION) walk(s.items);
  return routes;
}

export const routes: RouteObject[] = [
  {
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <PublicOnly />,
        children: [
          { path: 'login', element: lazyPage(auth, 'LoginPage') },
          { path: 'forgot-password', element: lazyPage(auth, 'ForgotPasswordPage') },
        ],
      },
      // Token pages work whether or not someone is signed in on this browser.
      { path: 'reset-password', element: lazyPage(auth, 'ResetPasswordPage') },
      { path: 'verify-email', element: lazyPage(auth, 'VerifyEmailPage') },
      { path: 'accept-invitation', element: lazyPage(auth, 'AcceptInvitationPage') },
      // Public secure records release (token in the URL fragment; no sign-in).
      {
        path: 'records-release',
        element: lazyPage(
          () => import('@/features/records/PublicReleasePage'),
          'PublicReleasePage',
        ),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },
              ...navigationRoutes(),
              {
                path: 'admin/schools/:schoolId',
                element: guarded(
                  { anyOf: ['school.read'] },
                  lazyPage(
                    () => import('@/features/admin/schools/SchoolDetailPage'),
                    'SchoolDetailPage',
                  ),
                ),
              },
              {
                path: 'transportation/routes/:routeId',
                element: guarded(
                  { anyOf: ['transportation.read'] },
                  lazyPage(
                    () => import('@/features/transportation/RouteDetailPage'),
                    'RouteDetailPage',
                  ),
                ),
              },
              {
                path: 'transportation/live/buses/:id',
                element: guarded(
                  { anyOf: ['transportation.live.read'] },
                  lazyPage(
                    () => import('@/features/transportation/BusDetailPage'),
                    'BusDetailPage',
                  ),
                ),
              },
              {
                path: 'transportation/live/trips/:id',
                element: guarded(
                  { anyOf: ['transportation.read', 'transportation.live.read'] },
                  lazyPage(
                    () => import('@/features/transportation/TripDetailPage'),
                    'TripDetailPage',
                  ),
                ),
              },
              {
                path: 'enrollment/forms/:formId',
                element: guarded(
                  { anyOf: ['forms.read', 'enrollment.read'] },
                  lazyPage(() => import('@/features/forms/FormBuilderPage'), 'FormBuilderPage'),
                ),
              },
              {
                path: 'enrollment/applications/:applicationId',
                element: guarded(
                  { anyOf: ['enrollment.read'] },
                  lazyPage(
                    () => import('@/features/enrollment/ApplicationDetailPage'),
                    'ApplicationDetailPage',
                  ),
                ),
              },
              {
                path: 'academics/classes/:sectionId',
                element: guarded(
                  { anyOf: ['class.read'] },
                  lazyPage(
                    () => import('@/features/academics/SectionDetailPage'),
                    'SectionDetailPage',
                  ),
                ),
              },
              {
                path: 'teaching/classes/:sectionId',
                element: guarded(
                  {
                    anyOf: [
                      'grades.read',
                      'grades.manage',
                      'lesson_plan.read',
                      'lesson_plan.manage',
                    ],
                  },
                  lazyPage(() => import('@/features/teaching/ClassPage'), 'ClassPage'),
                ),
              },
              ...['report-cards', 'progress-reports'].flatMap((base) => [
                {
                  path: `academics/${base}/periods/:periodId`,
                  element: guarded(
                    REPORT_CARD_ADMIN,
                    lazyPage(
                      () => import('@/features/report-cards/PeriodDashboardPage'),
                      'PeriodDashboardPage',
                    ),
                  ),
                },
                {
                  path: `academics/${base}/periods/:periodId/sections/:sectionId`,
                  element: guarded(
                    REPORT_CARD_ADMIN,
                    lazyPage(
                      () => import('@/features/report-cards/SectionReportPage'),
                      'SectionReportPage',
                    ),
                  ),
                },
              ]),
              {
                path: 'academics/report-cards/cards/:cardId',
                element: guarded(
                  CARD_READERS,
                  lazyPage(
                    () => import('@/features/report-cards/ReportCardDetailPage'),
                    'ReportCardDetailPage',
                  ),
                ),
              },
              {
                path: 'academics/report-cards/cards/:cardId/print',
                element: guarded(
                  CARD_READERS,
                  lazyPage(
                    () => import('@/features/report-cards/ReportCardPrintPage'),
                    'ReportCardPrintPage',
                  ),
                ),
              },
              {
                path: 'teaching/report-cards/:periodId/sections/:sectionId',
                element: guarded(
                  { anyOf: ['grades.read', 'grades.manage', 'report_card.manage'] },
                  lazyPage(
                    () => import('@/features/report-cards/SectionReportPage'),
                    'SectionReportPage',
                  ),
                ),
              },
              {
                path: 'academics/transcripts/view/:generationId',
                element: guarded(
                  RECORD_READERS,
                  lazyPage(
                    () => import('@/features/records/TranscriptViewPage'),
                    'TranscriptViewPage',
                  ),
                ),
              },
              {
                // Notification deep link target (communications: discipline.created).
                path: 'student-services/discipline/incidents/:incidentId',
                element: guarded(
                  { anyOf: ['discipline.read'] },
                  lazyPage(
                    () => import('@/features/discipline/IncidentDetailPage'),
                    'IncidentDetailPage',
                  ),
                ),
              },
              {
                path: 'student-services/discipline/detention/:sessionId',
                element: guarded(
                  { anyOf: ['discipline.read', 'discipline.manage', 'detention.supervise'] },
                  lazyPage(
                    () => import('@/features/discipline/DetentionSessionPage'),
                    'DetentionSessionPage',
                  ),
                ),
              },
              // Short alias for the active passes dashboard.
              { path: 'passes', element: <Navigate to="/student-services/hall-passes" replace /> },
              // Short alias for the visitor log.
              { path: 'visitors', element: <Navigate to="/front-office/visitors" replace /> },
              {
                // Calendar event deep link (feed `link`, notifications): the calendar with the
                // event's detail drawer open.
                path: 'calendar/events/:eventId',
                element: guarded({ anyOf: ['calendar.read'] }, CALENDAR_PAGE),
              },
              {
                path: 'student-services/student-ids/print-jobs/:jobId',
                element: guarded(
                  { allOf: ['student.read', 'student_id.manage'] },
                  lazyPage(
                    () => import('@/features/student-ids/PrintJobDetailPage'),
                    'PrintJobDetailPage',
                  ),
                ),
              },
              // Short alias for ID card batch printing.
              {
                path: 'student-ids/print-jobs',
                element: <Navigate to="/student-services/student-ids/print-jobs" replace />,
              },
              {
                path: 'student-services/nurse/visits/:visitId',
                element: guarded(
                  { allOf: ['student.read', 'student.health.read'] },
                  lazyPage(() => import('@/features/health/VisitDetailPage'), 'VisitDetailPage'),
                ),
              },
              {
                path: 'people/staff/:staffId',
                element: guarded(
                  { anyOf: ['staff.read'] },
                  lazyPage(() => import('@/features/staff/StaffDetailPage'), 'StaffDetailPage'),
                ),
              },
              {
                // Counselor view of one student (counseling.read; reads audited, 404 otherwise).
                path: 'student-services/guidance/students/:studentId',
                element: guarded(
                  { allOf: ['student.read', 'counseling.read'] },
                  lazyPage(
                    () => import('@/features/guidance/GuidanceStudentPage'),
                    'GuidanceStudentPage',
                  ),
                ),
              },
              {
                path: 'student-services/iep-504/:planId',
                element: guarded(
                  { allOf: ['student.read', 'iep.read'] },
                  lazyPage(
                    () => import('@/features/special-programs/PlanDetailPage'),
                    'PlanDetailPage',
                  ),
                ),
              },
              {
                // Records Department (DOCREC): cumulative record, request detail, letter print.
                path: 'records/students/:studentId',
                element: guarded(
                  { allOf: ['student.read', 'records.read'] },
                  lazyPage(
                    () => import('@/features/records/CumulativeRecordPage'),
                    'CumulativeRecordPage',
                  ),
                ),
              },
              {
                path: 'records/requests/:requestId',
                element: guarded(
                  { anyOf: ['records.read', 'records.requests.manage', 'records.release'] },
                  lazyPage(
                    () => import('@/features/records/RecordsRequestDetailPage'),
                    'RecordsRequestDetailPage',
                  ),
                ),
              },
              {
                path: 'records/letters/:letterId',
                element: guarded(
                  { anyOf: ['records.read'] },
                  lazyPage(
                    () => import('@/features/records/VerificationLetterPage'),
                    'VerificationLetterPage',
                  ),
                ),
              },
              {
                path: 'people/students/:studentId',
                element: guarded(
                  { anyOf: ['student.read'] },
                  lazyPage(
                    () => import('@/features/students/StudentProfilePage'),
                    'StudentProfilePage',
                  ),
                ),
              },
              {
                path: 'people/guardians/:guardianId',
                element: guarded(
                  { anyOf: ['guardian.read'] },
                  lazyPage(
                    () => import('@/features/guardians/GuardianDetailPage'),
                    'GuardianDetailPage',
                  ),
                ),
              },
              {
                path: 'people/households/:householdId',
                element: guarded(
                  { anyOf: ['guardian.read'] },
                  lazyPage(
                    () => import('@/features/households/HouseholdDetailPage'),
                    'HouseholdDetailPage',
                  ),
                ),
              },
              {
                path: 'admin/users/:userId',

                element: guarded(
                  { anyOf: ['user.read'] },
                  lazyPage(() => import('@/features/admin/users/UserDetailPage'), 'UserDetailPage'),
                ),
              },
              {
                // Notification deep link target (communications: budget.approval_requested /
                // budget.request_decided).
                path: 'budgets/purchase-requests/:id',
                element: guarded(
                  { anyOf: ['budget.read', 'budget.submit'] },
                  lazyPage(
                    () => import('@/features/budgets/RequestDetailPage'),
                    'RequestDetailPage',
                  ),
                ),
              },
              {
                path: 'account/profile',
                element: lazyPage(() => import('@/features/account/AccountPages'), 'ProfilePage'),
              },
              {
                path: 'account/security',
                element: lazyPage(() => import('@/features/account/AccountPages'), 'SecurityPage'),
              },
              {
                // Notification Center preferences (every member; category × channel).
                path: 'account/notifications',
                element: lazyPage(
                  () => import('@/features/account/NotificationPreferencesPage'),
                  'NotificationPreferencesPage',
                ),
              },
              {
                // Notification Center (every member; the bell's "View all").
                path: 'notifications',
                element: lazyPage(
                  () => import('@/features/notifications/NotificationsPage'),
                  'NotificationsPage',
                ),
              },
              {
                // Communication Center message detail (sender or communications.manage; 404
                // otherwise — the API decides).
                path: 'communications/messages/:id',
                element: guarded(
                  {
                    anyOf: [
                      'communications.send',
                      'communications.send.class',
                      'communications.broadcast',
                      'communications.staff',
                      'communications.templates.manage',
                      'communications.manage',
                    ],
                  },
                  lazyPage(
                    () => import('@/features/communications/MessageDetailPage'),
                    'MessageDetailPage',
                  ),
                ),
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
